import {
  Card,
  Divider,
  Grid,
  Group,
  rem,
  Select,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ITApprovalProcessInfo,
  ITAuthObject,
} from "@/dto/international-transfer/international-transfer";

type ApprovalInfoProps = {
  approvalProcessInfo?: ITApprovalProcessInfo | null;
  authObjects?: ITAuthObject[];
  onChangeAuthType?: (authType: string) => void;
};

const ApprovalInfo = ({
  approvalProcessInfo,
  authObjects,
  onChangeAuthType,
}: ApprovalInfoProps) => {
  const { t } = useTranslation();

  // Danh sách người duyệt (đầy đủ list, không chỉ người đầu tiên)
  const approverList = useMemo(() => {
    const list =
      approvalProcessInfo?.nextLevelApprovers ||
      approvalProcessInfo?.lastApprovers ||
      [];
    return list.map((a) => ({
      id: a.approvalUserId,
      name: a.approvalUserFullName,
    }));
  }, [approvalProcessInfo]);

  const authDataList = useMemo(() => {
    if (authObjects && authObjects.length > 0) {
      return authObjects.map((a) => ({
        value: a.authType,
        label: a.authDes || a.authType,
      }));
    }
    // Fallback khi BE chưa trả authObjects: mặc định đủ SMS OTP + Smart OTP
    // (khớp getApproveAuthType luôn trả cả 2). Chỉ áp cho luồng CTQT.
    return [
      { value: "1", label: "SMS OTP" },
      { value: "2", label: "Smart OTP" },
    ];
  }, [authObjects]);

  const [authMode, setAuthMode] = useState<string>(
    authDataList[0]?.value || "1",
  );

  // Đồng bộ giá trị mặc định lên container ngay khi có authObjects — để container
  // dùng đúng phương thức (SMS/Smart OTP) kể cả khi người dùng không mở dropdown.
  useEffect(() => {
    const initial = authDataList[0]?.value || "1";
    setAuthMode(initial);
    onChangeAuthType?.(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authDataList]);

  return (
    <Card shadow="sm" radius="md" pb={rem(50)} w="100%" h="auto">
      <Card.Section inheritPadding py="xs">
        <Group position="apart">
          <Text fz="16px" weight={600}>
            {t("APPROVAL_INFORMATION")}
          </Text>
        </Group>
      </Card.Section>
      <Card.Section inheritPadding py="xs">
        <Grid>
          <Grid.Col span={6}>
            <Text fz={14} c="#6D6D6D">
              {t("APPROVAL_ID")}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text fw={500} ta="right">
              {approvalProcessInfo?.approvalProcessId || "—"}
            </Text>
          </Grid.Col>
        </Grid>
        <Space h="md" />
        <Grid>
          <Grid.Col span={6}>
            <Text c="#6D6D6D" fz={14}>
              {t("APPROVAL")}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text fw={500} ta="right">
              {approvalProcessInfo?.numberOfApprovals || "—"}
            </Text>
          </Grid.Col>
        </Grid>
        <Space h="md" />
        <Grid>
          <Grid.Col span={5}>
            <Text c="#6D6D6D" fz={14}>
              {t("REVIEWER")}
            </Text>
          </Grid.Col>
          <Grid.Col span={7}>
            {approverList.length === 0 ? (
              <Text fw={500} ta="right">
                —
              </Text>
            ) : (
              <Stack spacing={4} align="flex-end">
                {approverList.map((item) => (
                  <Text key={item.id} weight={600} color="#0047D0" ta="right">
                    {item.name}
                  </Text>
                ))}
              </Stack>
            )}
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Divider my="sm" variant="dashed" />
      <Card.Section inheritPadding py="xs">
        <Select
          label={t("AUTHENTICATION_METHOD")}
          styles={{ label: { fontWeight: 600, fontSize: "16px" } }}
          value={authMode}
          onChange={(value) => {
            const next = value || "1";
            setAuthMode(next);
            onChangeAuthType?.(next);
          }}
          data={authDataList}
          mb="sm"
          name="authType"
          dropdownPosition="bottom"
          // Render dropdown qua portal + zIndex cao để không bị Card/footer che khuất
          withinPortal
          zIndex={400}
          rightSection={<IconChevronDown size="1rem" />}
        />
      </Card.Section>
    </Card>
  );
};

export default ApprovalInfo;
